import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
} from '@mui/material';
import { DashboardLayout } from '../components/DashboardLayout';
import api from '../services/api';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { AlertDialog } from '@components/common/AlertDialog.tsx';
import { AdaptiveTabs } from '@components/common/AdaptiveTabs.tsx';
import { DevSubscriptionTools } from '../components/settings/DevSubscriptionTools';
import { SchedulerManager } from '../components/development/SchedulerManager';
import { DevEventGenerator } from '../components/development/DevEventGenerator';
import { DevAiTest } from '../components/development/DevAiTest';
import { DevBatchSyncTest } from '../components/development/DevBatchSyncTest';
import { DevDemoContentDiagnostics } from '../components/development/DevDemoContentDiagnostics';
import { DevDatabaseDump } from '../components/development/DevDatabaseDump';
import { DevAmplitudeTools } from '../components/development/DevAmplitudeTools';
import { PageIndices, TabIndices } from '../common/i-doc/i-doc.registry';
import { PageHint } from '@components/common/PageHint.tsx';

const TABS = [
  { value: 'tools', label: 'Інструменти' },
  { value: 'subscription', label: 'Підписка' },
  { value: 'scheduler', label: 'Планувальник (Cron)' },
  { value: 'event-generator', label: 'Генератор подій' },
  { value: 'demo-content', label: 'Demo-акаунти' },
  { value: 'ai-test', label: 'Тестування AI' },
  { value: 'batch-sync', label: 'Пакетна синхронізація' },
  { value: 'database-dump', label: 'Дамп БД' },
  { value: 'amplitude', label: 'Amplitude' },
];

const GeneralToolsTab: React.FC = () => {
  const [emailUserId, setEmailUserId] = useState('');
  const [surveyUserId, setSurveyUserId] = useState('');
  const [deleteUserEmail, setDeleteUserEmail] = useState('');

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ title: '', message: '' });

  const showAlert = (title: string, message: string) => {
    setAlertInfo({ title, message });
    setIsAlertDialogOpen(true);
  };

  const getErrorMessage = (error: any): string => {
    if (error.response && error.response.data) {
      const data = error.response.data;

      if (data.message) {
        const message = Array.isArray(data.message)
          ? data.message.join('\n')
          : data.message;

        if (data.statusCode && data.error) {
          return `${data.statusCode} ${data.error}: ${message}`;
        }
        return message;
      }
    }
    return error.message || 'An unknown error occurred.';
  };

  const handleResetEmailVerification = () => {
    if (!emailUserId) {
      showAlert('Error', 'Please enter a User ID.');
      return;
    }
    api.get(`/developer/reset-email-verification/${emailUserId}`)
      .then(response => {
        showAlert('Success', response.data.message);
      })
      .catch(error => {
        console.error('Error resetting email verification:', error);
        showAlert('Error', getErrorMessage(error));
      });
  };

  const handleResetSurveyCompletion = () => {
    if (!surveyUserId) {
      showAlert('Error', 'Please enter a User ID.');
      return;
    }
    api.get(`/developer/reset-survey-completion/${surveyUserId}`)
      .then(response => {
        showAlert('Success', response.data.message);
      })
      .catch(error => {
        console.error('Error resetting survey completion:', error);
        showAlert('Error', getErrorMessage(error));
      });
  };

  const handleDeleteUserClick = () => {
    if (!deleteUserEmail) {
      showAlert('Error', 'Please enter a user email.');
      return;
    }
    setIsConfirmDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    setIsConfirmDialogOpen(false);
    if (!deleteUserEmail) return;

    api.delete(`/developer/delete-user-by-email/${deleteUserEmail}`)
      .then(response => {
        showAlert('Success', response.data.message);
        setDeleteUserEmail('');
      })
      .catch(error => {
        console.error('Error deleting user:', error);
        showAlert('Error', getErrorMessage(error));
      });
  };

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Скинути верифікацію пошти</Typography>
        <TextField
          label="User ID"
          variant="outlined"
          value={emailUserId}
          onChange={(e) => setEmailUserId(e.target.value)}
          sx={{ mr: 2 }}
        />
        <Button variant="contained" onClick={handleResetEmailVerification}>
          Скинути
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Скинути проходження опитування</Typography>
        <TextField
          label="User ID"
          variant="outlined"
          value={surveyUserId}
          onChange={(e) => setSurveyUserId(e.target.value)}
          sx={{ mr: 2 }}
        />
        <Button variant="contained" onClick={handleResetSurveyCompletion}>
          Скинути
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box>
        <Typography variant="h6">Видалити користувача</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Ця дія є незворотною і повністю видалить користувача та всі пов'язані з ним дані.
        </Typography>
        <TextField
          label="User Email"
          variant="outlined"
          value={deleteUserEmail}
          onChange={(e) => setDeleteUserEmail(e.target.value)}
          sx={{ mr: 2 }}
        />
        <Button variant="contained" color="error" onClick={handleDeleteUserClick}>
          Видалити
        </Button>
      </Box>

      <ConfirmationDialog
        open={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Підтвердження видалення"
        message={`Ви впевнені, що хочете видалити користувача з email: ${deleteUserEmail}? Ця операція є незворотною.`}
        confirmText="Видалити"
        cancelText="Скасувати"
      />

      <AlertDialog
        open={isAlertDialogOpen}
        onClose={() => setIsAlertDialogOpen(false)}
        title={alertInfo.title}
        message={alertInfo.message}
      />
    </>
  );
};

const DevelopmentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') ?? 'tools';
  const activeTab = TABS.some(t => t.value === rawTab) ? rawTab : 'tools';

  return (
    <DashboardLayout onChangePassword={() => {}} data-i-doc={PageIndices.DEVELOPMENT}>
      <Container maxWidth="lg">
        <PageHint iDoc={PageIndices.DEVELOPMENT} />
        <Paper sx={{ mt: 3, p: 2 }}>
          <AdaptiveTabs tabs={TABS} searchParamKey="tab" defaultValue="tools" />

          <Box sx={{ py: 3 }}>
            {activeTab === 'tools' && (
              <Box data-i-doc={TabIndices.DEVELOPMENT_TOOLS}>
                <PageHint iDoc={TabIndices.DEVELOPMENT_TOOLS} />
                <GeneralToolsTab />
              </Box>
            )}
            {activeTab === 'subscription' && (
              <Box data-i-doc={TabIndices.DEVELOPMENT_SUBSCRIPTION}>
                <PageHint iDoc={TabIndices.DEVELOPMENT_SUBSCRIPTION} />
                <DevSubscriptionTools />
              </Box>
            )}
            {activeTab === 'scheduler' && (
              <Box data-i-doc={TabIndices.DEVELOPMENT_SCHEDULER}>
                <PageHint iDoc={TabIndices.DEVELOPMENT_SCHEDULER} />
                <SchedulerManager />
              </Box>
            )}
            {activeTab === 'event-generator' && (
              <Box data-i-doc={TabIndices.DEVELOPMENT_EVENT_GEN}>
                <PageHint iDoc={TabIndices.DEVELOPMENT_EVENT_GEN} />
                <DevEventGenerator />
              </Box>
            )}
            {activeTab === 'demo-content' && (
              <Box data-i-doc={TabIndices.DEVELOPMENT_DEMO_CONTENT}>
                <PageHint iDoc={TabIndices.DEVELOPMENT_DEMO_CONTENT} />
                <DevDemoContentDiagnostics />
              </Box>
            )}
            {activeTab === 'ai-test' && (
              <Box data-i-doc={TabIndices.DEVELOPMENT_AI_TEST}>
                <PageHint iDoc={TabIndices.DEVELOPMENT_AI_TEST} />
                <DevAiTest />
              </Box>
            )}
            {activeTab === 'batch-sync' && (
              <Box data-i-doc={TabIndices.DEVELOPMENT_BATCH_SYNC}>
                <PageHint iDoc={TabIndices.DEVELOPMENT_BATCH_SYNC} />
                <DevBatchSyncTest />
              </Box>
            )}
            {activeTab === 'database-dump' && (
              <Box data-i-doc={TabIndices.DEVELOPMENT_DATABASE_DUMP}>
                <PageHint iDoc={TabIndices.DEVELOPMENT_DATABASE_DUMP} />
                <DevDatabaseDump />
              </Box>
            )}
            {activeTab === 'amplitude' && (
              <Box data-i-doc={TabIndices.DEVELOPMENT_AMPLITUDE}>
                <PageHint iDoc={TabIndices.DEVELOPMENT_AMPLITUDE} />
                <DevAmplitudeTools />
              </Box>
            )}
          </Box>
        </Paper>
      </Container>
    </DashboardLayout>
  );
};

export default DevelopmentPage;
